<script setup>
import {useUsersStore} from "@/stores"
import { mediaUrl } from '@core/utils/mediaUrl'
import {ref} from "vue"
import axios from "@axios"
import CmsText from '@/@core/components/CmsText.vue'
import { REGULAR_ANNUAL_PRICE_DOLLARS } from '@/utils/membershipPricing'

const props = defineProps({
  showSearch: {
    type: Boolean,
    default: false
  }
})

const usersStore = useUsersStore()
let myUser = null
let loginDialogMode = null;
({myUser, loginDialogMode} = storeToRefs(usersStore))

const tab = ref('how-rm-works')

// Testimonials data
const testimonials = ref([
  {
    profileId: 'justinishbia',
    title: 'Managing Partner - Shore Capital Partners',
    subtitle: 'Alternate Governor - Phoenix Suns',
    quote: 'RM is awesome - everyone on my team across the country is a member & loves it. We use for business dinners & personally. Makes it easy for my wife & I to get last minute date nights at our favorite places & she says so fun!',
    profileData: null
  },
  {
    profileId: 'marinasquerciati',
    title: 'Actor - NBC Universal',
    subtitle: null,
    quote: 'I use RM for last-minute reservations in Chicago and all over the world. I love how convenient it is. I recommended RM to my co-stars and they\'re members too!',
    profileData: null
  },
  {
    profileId: 'tonydavis',
    title: 'Co-Founder and President - Linden Capital Partners',
    subtitle: null,
    quote: 'RM is one of the best employee benefits we\'ve provided. Having restaurateurs friends & investing in restaurants, I already enjoyed great access - now with RM, my team does, too. I also don\'t have to text my friends anymore!',
    profileData: null
  }
])

// Fetch profile data for testimonials
const fetchTestimonialProfiles = async () => {
  for (const testimonial of testimonials.value) {
    try {
      const response = await axios.get(`/user/?profileId=${testimonial.profileId}`)
      if (response.data && response.data.length > 0) {
        testimonial.profileData = response.data[0]
      }
    } catch (error) {
      console.error(`Error fetching profile for ${testimonial.profileId}:`, error)
    }
  }
}

// Fetch profiles on mount
onMounted(() => {
  fetchTestimonialProfiles()
})

// Get profile image URL
const getProfileImageUrl = profileImageId => {
  return mediaUrl(profileImageId, 'small')
}

// Navigate to profile
const goToProfile = profileId => {
  window.open(`https://reserverm.com/m/${profileId}`, '_blank')
}
</script>

<template>
  <div>
    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n4"
    >
      <div class="back-button-container">
        <RouterLink
          :to="`/m/${myUser?.profileId}`"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Profile</span>
        </RouterLink>
      </div>
    </div>
    <section class="home-masthead">
      <div class="content-wrapper ">
        <div class="masthead-content mt-4 mb-3 pl-0 pr-0">
          <CmsText
            k="members.masthead"
            tag="h1"
            class="black-text title-text mb-2"
          />
          
          <template v-if="(!myUser || myUser.userLevel == 'dummy')">
            <div class="mt-4">
              <VBtn
                variant="flat"
                class=""
                size="small"
                color="primary"
                @click="loginDialogMode = 'activate';"
              >
                <span>Request to join</span>
              </VBtn>
            </div>
            <!--
              <p class="black-text text-caption mb-0">
              Explore for free <br>
              Upgrade to <RouterLink class="text-primary" :to="{ path: '/members' }">Platinum</RouterLink> anytime (${{ REGULAR_ANNUAL_PRICE_DOLLARS }}/year) <br>
              </p> 
            -->
          </template>
        </div>
      </div>
    </section>
    <VDivider thickness="2" />
    <div
      style="display: flex;"
      class="justify-center mt-5 mb-8"
    >
      <div class="content-wrapper tab-content-width">
        <VCard class="px-5 py-6 pb-4">
          <div
            style="display: flex;"
            class="justify-center"
          >
            <VTabs
              v-model="tab"
              class="tab-height full-width-tabs"
              grow
            >
              <VTab
                value="how-rm-works"
                class="tab-height text-xs"
              >
                <CmsText
                  k="members.tabs.howRmWorksTitle"
                  tag="span"
                />
              </VTab>
              <VTab
                value="faqs"
                class="tab-height text-xs"
              >
                <CmsText
                  k="members.tabs.faqsTitle"
                  tag="span"
                />
              </VTab>
            </VTabs>
          </div>
          
          <VDivider />
          
          <VWindow v-model="tab">
            <VWindowItem
              class="pt-4 text-xs"
              value="how-rm-works"
            >
              <div class="content-wrapper tab-content-width">
                <CmsText
                  k="members.tabs.howRmWorks"
                  tag="div"
                  class="section-subtitle text-left mt-2 how-rm-works-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>
            
            <VWindowItem
              class="pt-4 text-xs"
              value="faqs"
            >
              <div class="content-wrapper tab-content-width">
                <CmsText
                  k="members.tabs.faqs"
                  tag="div"
                  class="section-subtitle text-left mt-2 faqs-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>
          </VWindow>
          
          <VDivider class="mt-6 mb-6" />
          
          <div class="testimonials-section mb-2">
            <h3
              class="text-sm text-center mb-4"
              style="font-weight: 600; color: rgba(0, 0, 0, 0.6);"
            >
              <CmsText
                k="members.testimonialsTitle"
                tag="span"
              />
            </h3>
            
            <div
              v-for="(testimonial, index) in testimonials"
              :key="testimonial.profileId"
              :class="{ 'mt-4': index > 0 }"
            >
              <div class="testimonial-item pa-4">
                <div class="d-flex align-start gap-3 mb-3">
                  <div 
                    class="profile-photo-wrapper"
                    style="cursor: pointer;"
                    @click="goToProfile(testimonial.profileId)"
                  >
                    <VAvatar
                      v-if="testimonial.profileData?.profileImageId"
                      size="60"
                      class="profile-avatar"
                    >
                      <VImg
                        :src="getProfileImageUrl(testimonial.profileData.profileImageId)"
                        :alt="testimonial.profileData.fullName"
                        cover
                      />
                    </VAvatar>
                    <VAvatar
                      v-else
                      size="60"
                      color="grey-lighten-2"
                    >
                      <VIcon>mdi-account</VIcon>
                    </VAvatar>
                  </div>
                  <div
                    class="flex-grow-1"
                    style="margin-top: -15px;"
                  >
                    <div 
                      class="testimonial-name"
                      style="cursor: pointer; font-weight: 600; font-size: 0.875rem; line-height: 1.2; color: rgba(0, 0, 0, 0.6);"
                      @click="goToProfile(testimonial.profileId)"
                    >
                      {{ testimonial.profileData?.fullName || 'Member' }}
                    </div>
                    <div
                      class="testimonial-title text-xs"
                      style="color: rgba(0, 0, 0, 0.6); line-height: 1.3; font-weight: 600;"
                    >
                      {{ testimonial.title }}<span v-if="testimonial.subtitle"><br>{{ testimonial.subtitle }}</span>
                    </div>
                  </div>
                </div>
                <div
                  class="testimonial-quote text-xs"
                  style="line-height: 1.4; font-style: italic;"
                >
                  "{{ testimonial.quote }}"
                </div>
              </div>
            </div>
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title-text {
    font-size: 1rem;
    font-weight: 400;
    margin: 0;
    color: var(--ds-text-strong);
    text-align: center;
  }

  // Bold is controlled by the CMS rich-text content (<b>/<strong>), not forced
  // on the whole masthead — so editors can bold/unbold individual phrases.
  .title-text :deep(b),
  .title-text :deep(strong) {
    font-weight: 700;
  }

  .masthead-content-sub-headline {
    font-family: var(--heading-font);
    font-weight: 400;
    color: #fff;
    font-size: 1.5rem;
    line-height: 1em;
  }
  .masthead-content-logo{
    height: 2.5rem;
    width: 2.5rem;
  }
  .masthead-content{
    margin-top: 1.5em;
  }

  .overview-title{
    font-weight: 600;
    color: var(--ds-text-strong);
    font-size: 1.2rem;
    line-height: 1em;
    margin-top: 0.5em;
  }

  .tab-content-width {
    max-width: 460px;
    font-size: 0.8em;
  }

  .testimonials-section {
    padding: 0;
  }

  .testimonial-item {
    display: block;
  }

  .profile-photo-wrapper {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  .profile-photo-wrapper:hover {
    transform: scale(1.05);
  }

  .profile-avatar {
    border: 2px solid var(--ds-border-default);
  }

  .testimonial-name:hover {
    text-decoration: underline;
  }

  .testimonial-title {
    margin-top: 2px;
  }

  .testimonial-quote {
    color: rgba(0, 0, 0, 0.6);
  }

  .full-width-tabs {
    width: 100%;
  }

  .full-width-tabs .v-tab {
    flex: 1;
    max-width: 50%;
    justify-content: center;
  }

  .full-width-tabs .v-tab .v-btn__content {
    color: var(--ds-text-strong) !important;
  }

  .how-rm-works-content {
    font-family: inherit !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .how-rm-works-content :deep(p) {
    margin: 0 !important;
    font-family: inherit !important;
    font-size: 0.75rem !important;
    line-height: 16px !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .how-rm-works-content :deep(b) {
    font-family: inherit !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .how-rm-works-content :deep(a) {
    color: rgb(var(--v-theme-primary));
  }

  .how-rm-works-content :deep(p.section-title) {
    margin-top: 1rem !important;
  }

  .how-rm-works-content :deep(p.section-title:first-child) {
    margin-top: 0 !important;
  }

  .faqs-content {
    font-family: inherit !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .faqs-content :deep(p) {
    margin: 0 !important;
    font-family: inherit !important;
    font-size: 0.75rem !important;
    line-height: 16px !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .faqs-content :deep(b) {
    font-family: inherit !important;
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .faqs-content :deep(a) {
    color: rgb(var(--v-theme-primary));
  }

  .faqs-content :deep(p.faq-title) {
    margin-top: 1rem !important;
  }

  .faqs-content :deep(p.faq-title:first-child) {
    margin-top: 0 !important;
  }

  .faqs-content :deep(p.faq-subsection) {
    margin-top: 0.5rem !important;
  }

  .back-button-container {
    padding: 0 4px;
  }

  .back-button {
    padding: 8px 12px;
    color: rgb(51, 51, 51);
    text-decoration: none;
    font-size: 14px;
    transition: background-color 0.2s;
    border-radius: 4px;
    display: inline-flex;
  }

  .back-button:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .back-button svg {
    color: rgb(102, 102, 102);
  }
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: false
</route>
